import type { OrderFiscalCommandLogEntryInput } from '@shared/api/graphql/gql/graphql';

const DEFAULT_BATCH_SIZE = 50;
const DEFAULT_FLUSH_INTERVAL_MS = 10_000;
const DEFAULT_MAX_RETRIES = 5;
const DEFAULT_RETRY_BASE_MS = 1_000;
const DEFAULT_RETRY_CEILING_MS = 30_000;

export interface KkmJournalBufferedEntry {
  entry: OrderFiscalCommandLogEntryInput;
  locationId: string;
  organizationId: string;
}

interface KkmJournalBufferDependencies {
  batchSize?: number;
  clearTimeout?: typeof globalThis.clearTimeout;
  flushIntervalMs?: number;
  maxRetries?: number;
  retryBaseMs?: number;
  retryCeilingMs?: number;
  send: (batch: readonly KkmJournalBufferedEntry[]) => Promise<void>;
  sendBeacon: (batch: readonly KkmJournalBufferedEntry[]) => boolean;
  setTimeout?: typeof globalThis.setTimeout;
  warn?: (count: number) => void;
}

export interface KkmJournalBuffer {
  flush(): Promise<void>;
  flushWithBeacon(): void;
  pendingCount(): number;
  record(entry: KkmJournalBufferedEntry): void;
}

export function createKkmJournalBuffer(dependencies: KkmJournalBufferDependencies): KkmJournalBuffer {
  const batchSize = dependencies.batchSize ?? DEFAULT_BATCH_SIZE;
  const clearTimer = dependencies.clearTimeout ?? globalThis.clearTimeout;
  const flushIntervalMs = dependencies.flushIntervalMs ?? DEFAULT_FLUSH_INTERVAL_MS;
  const maxRetries = dependencies.maxRetries ?? DEFAULT_MAX_RETRIES;
  const retryBaseMs = dependencies.retryBaseMs ?? DEFAULT_RETRY_BASE_MS;
  const retryCeilingMs = dependencies.retryCeilingMs ?? DEFAULT_RETRY_CEILING_MS;
  const scheduleTimer = dependencies.setTimeout ?? globalThis.setTimeout;
  const warn = dependencies.warn ?? warnDroppedBatch;
  const queue: KkmJournalBufferedEntry[] = [];
  let currentFlush: Promise<void> | null = null;
  let retryAttempt = 0;
  let timer: ReturnType<typeof setTimeout> | null = null;

  const clearScheduledFlush = (): void => {
    if (timer !== null) {
      clearTimer(timer);
      timer = null;
    }
  };

  const scheduleFlush = (delayMs: number): void => {
    clearScheduledFlush();

    if (queue.length === 0) {
      return;
    }

    timer = scheduleTimer(() => {
      timer = null;
      void flush();
    }, delayMs);
  };

  const selectBatch = (): KkmJournalBufferedEntry[] => {
    const scope = queue[0];

    if (scope === undefined) {
      return [];
    }

    return queue
      .filter((item) => item.organizationId === scope.organizationId && item.locationId === scope.locationId)
      .slice(0, batchSize);
  };

  const removeBatch = (batch: readonly KkmJournalBufferedEntry[]): void => {
    const identities = new Set(batch.map(bufferedEntryIdentity));

    for (let index = queue.length - 1; index >= 0; index -= 1) {
      const queued = queue[index];

      if (queued !== undefined && identities.has(bufferedEntryIdentity(queued))) {
        queue.splice(index, 1);
      }
    }
  };

  const runFlush = async (): Promise<void> => {
    clearScheduledFlush();
    const batch = selectBatch();

    if (batch.length === 0) {
      return;
    }

    /** Батч покидает очередь и при успехе, и при исчерпании попыток. */
    const finishBatch = (): number => {
      removeBatch(batch);
      retryAttempt = 0;

      return queue.length >= batchSize ? 0 : flushIntervalMs;
    };

    let nextDelayMs = flushIntervalMs;

    try {
      await dependencies.send(batch);
      nextDelayMs = finishBatch();
    } catch {
      retryAttempt += 1;

      if (retryAttempt > maxRetries) {
        warn(batch.length);
        nextDelayMs = finishBatch();
      } else {
        nextDelayMs = Math.min(retryBaseMs * 2 ** (retryAttempt - 1), retryCeilingMs);
      }
    } finally {
      currentFlush = null;
      scheduleFlush(nextDelayMs);
    }
  };

  const flush = (): Promise<void> => {
    if (currentFlush !== null) {
      return currentFlush;
    }

    currentFlush = runFlush();

    return currentFlush;
  };

  const flushWithBeacon = (): void => {
    clearScheduledFlush();

    while (queue.length > 0) {
      const batch = selectBatch();
      let accepted = false;

      try {
        accepted = batch.length > 0 && dependencies.sendBeacon(batch);
      } catch {
        accepted = false;
      }

      if (!accepted) {
        break;
      }

      removeBatch(batch);
      retryAttempt = 0;
    }

    scheduleFlush(flushIntervalMs);
  };

  return {
    flush,
    flushWithBeacon,
    pendingCount: () => queue.length,
    record(entry): void {
      queue.push(entry);

      if (queue.length >= batchSize && retryAttempt === 0) {
        void flush();
      } else if (timer === null && currentFlush === null) {
        scheduleFlush(flushIntervalMs);
      }
    },
  };
}

function bufferedEntryIdentity(item: KkmJournalBufferedEntry): string {
  return `${item.organizationId}:${item.locationId}:${item.entry.clientEventId}`;
}

function warnDroppedBatch(count: number): void {
  // Только факт исчерпания retry без тел журнала.
  console.warn('[kkm-journal] Исчерпаны попытки отправки батча.', { count });
}
